/**
 * 温馨治愈风格操作日志页面
 * 使用柔和配色、圆角、轻柔动画
 */

import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Space,
  Card,
  Form,
  Input,
  Modal,
  message,
  Tag,
  DatePicker,
  Select,
  Row,
  Col,
  Typography
} from 'antd';
import {
  DeleteOutlined,
  SearchOutlined,
  FileTextOutlined,
  CloseCircleOutlined,
  CheckCircleOutlined,
  FilterOutlined
} from '@ant-design/icons';
import { getOperLogPage, deleteOperLogs } from '../services/api/log';
import { OperLogStatus } from '../types/log';
import SearchBar from '../components/common/SearchBar';
import EmptyWarm from '../components/common/EmptyWarm';
import LoadingWarm from '../components/common/LoadingWarm';
import './OperationLog.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

const OperationLog: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [current, setCurrent] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchParams, setSearchParams] = useState({
    module: '',
    operName: '',
    businessType: undefined as string | undefined,
    status: undefined as number | undefined,
    startTime: '',
    endTime: ''
  });
  const [filterVisible, setFilterVisible] = useState(false);
  const [filterForm] = Form.useForm();

  // 获取日志列表
  const fetchLogs = async () => {
    setLoading(true);
    try {
      const res = await getOperLogPage({
        current,
        size: pageSize,
        ...searchParams
      });
      setLogs(res.records || []);
      setTotal(res.total || 0);
    } catch (error) {
      console.error('获取操作日志失败:', error);
      message.error('获取操作日志失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [current, pageSize, searchParams]);

  // 删除选中的日志
  const handleDeleteSelected = async () => {
    Modal.confirm({
      title: '确认删除',
      content: '确定要删除选中的操作日志吗？',
      okText: '确定',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const selectedIds = logs.filter(log => log.selected).map(log => log.id);
          if (selectedIds.length === 0) {
            message.warning('请先选择要删除的日志');
            return;
          }
          await deleteOperLogs(selectedIds);
          message.success('日志删除成功');
          fetchLogs();
        } catch (error) {
          console.error('删除日志失败:', error);
          message.error('删除日志失败');
        }
      }
    });
  };

  // 应用筛选条件
  const handleApplyFilter = async () => {
    try {
      const values = await filterForm.validateFields();
      const startDate = values.dateRange?.[0];
      const endDate = values.dateRange?.[1];

      setSearchParams({
        module: values.module || '',
        operName: values.operName || '',
        businessType: values.businessType || undefined,
        status: values.status !== undefined ? Number(values.status) : undefined,
        startTime: startDate ? startDate.format('YYYY-MM-DD HH:mm:ss') : '',
        endTime: endDate ? endDate.format('YYYY-MM-DD HH:mm:ss') : ''
      });
      setCurrent(1);
      setFilterVisible(false);
    } catch (error) {
      console.error('筛选失败:', error);
      message.error('筛选条件填写有误');
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '📁 模块',
      dataIndex: 'module',
      key: 'module',
      width: 120,
      ellipsis: true
    },
    {
      title: '📝 业务类型',
      dataIndex: 'businessType',
      key: 'businessType',
      width: 120,
      render: (type: string) => {
        // 后端返回的是中文，直接显示
        return type || '-';
      }
    },
    {
      title: '👤 操作人员',
      dataIndex: 'operName',
      key: 'operName',
      width: 100
    },
    {
      title: '📍 地点',
      dataIndex: 'operLocation',
      key: 'operLocation',
      width: 150,
      ellipsis: true
    },
    {
      title: '🌐 IP',
      dataIndex: 'operIp',
      key: 'operIp',
      width: 130,
      ellipsis: true
    },
    {
      title: '🔧 状态',
      dataIndex: 'status',
      key: 'status',
      width: 80,
      render: (status: number) => (
        <Tag icon={status === 1 ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={status === 1 ? 'success' : 'error'}>
          {status === 1 ? '成功' : '失败'}
        </Tag>
      )
    },
    {
      title: '📅 操作时间',
      dataIndex: 'operTime',
      key: 'operTime',
      width: 180,
      render: (time: string) => (
        <Typography.Text type="secondary">
          {time}
        </Typography.Text>
      )
    }
  ];

  return (
    <div className="operation-log-container">
      <Card
        className="warm-card"
        title={
          <Space>
            <FileTextOutlined className="operation-log-title-icon" />
            <span>操作日志</span>
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<FilterOutlined />}
              onClick={() => setFilterVisible(true)}
            >
              🌸 筛选
            </Button>
          </Space>
        }
        bordered={false}
      >
        <div className="operation-log-search-bar">
          <SearchBar
            placeholder="搜索模块或操作人员..."
            onSearch={(value) => {
              setSearchParams({ ...searchParams, module: value });
              setCurrent(1);
            }}
            width={320}
          />
        </div>

        {loading ? (
          <LoadingWarm type="cloud" tip="日志数据正在路上~" />
        ) : (
          <Table
            dataSource={logs}
            columns={columns}
            rowKey="id"
            pagination={{
              current,
              pageSize,
              total,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条`,
              onChange: (page, size) => {
                setCurrent(page);
                setPageSize(size || 10);
              }
            }}
            scroll={{ x: 1200 }}
            locale={{ emptyText: <EmptyWarm type="search" /> }}
          />
        )}
      </Card>

      {/* 筛选条件模态框 */}
      <Modal
        title="🔍 筛选条件"
        open={filterVisible}
        onOk={handleApplyFilter}
        onCancel={() => setFilterVisible(false)}
        okText="应用"
        cancelText="取消"
        width={600}
        className="warm-modal"
      >
        <Form form={filterForm} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="module"
                label="📁 模块标题"
              >
                <Input placeholder="请输入模块标题" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="operName"
                label="👤 操作人员"
              >
                <Input placeholder="请输入操作人员" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                name="businessType"
                label="📝 业务类型"
              >
                <Select placeholder="请选择业务类型" allowClear>
                  <Option value="新增">新增</Option>
                  <Option value="更新">更新</Option>
                  <Option value="删除">删除</Option>
                  <Option value="查询">查询</Option>
                  <Option value="其他">其他</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="status"
                label="🔧 状态"
              >
                <Select placeholder="请选择状态" allowClear>
                  <Option value={1}>✅ 成功</Option>
                  <Option value={0}>❌ 失败</Option>
                </Select>
              </Form.Item>
            </Col>

            <Col span={8}>
              <Form.Item
                name="dateRange"
                label="📅 时间范围"
              >
                <RangePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  placeholder={['开始时间', '结束时间']}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default OperationLog;
